import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, LockKeyholeCircle } from "@untitledui/icons";
import { useMemo, useState } from "react";
import z from "zod";
import { PasswordRequirementsChecklist } from "@/components/Atoms/PasswordRequirementsChecklist/PasswordRequirementsChecklist";
import { Button } from "@/components/base/buttons/button";
import { useAppForm } from "@/components/form";
import { createPasswordComplexitySchema } from "@/components/form/validation/create-common-validators";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { HeaderNavigation } from "@/components/Molecules/HeaderNav/HeaderNav";
import type { AppTranslateFn } from "@/hooks/use-app-translation";
import { useAppTranslation } from "@/hooks/use-app-translation";
import {
	evaluatePasswordRequirementFlags,
	PASSWORD_REQUIREMENT_RULE_ORDER,
	type PasswordRequirementRuleId,
	userService,
} from "@/services/user";
import { useResetPassword } from "@/services/user/user.mutations";

// Search params schema for reset password link
const searchSchema = z.object({
	state: z.enum(["valid", "expired", "invalid", "error"]).catch("valid"),
	resetToken: z.string().catch(""),
	userId: z.string().catch(""),
});

const createNewPasswordSchema = (t: AppTranslateFn) =>
	createPasswordComplexitySchema(t, 12);

const createPasswordFormSchema = (t: AppTranslateFn) =>
	z
		.object({
			newPassword: createNewPasswordSchema(t),
			confirmPassword: z
				.string()
				.min(1, t("common.validation.passwordConfirmRequired")),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: t("common.validation.passwordsDoNotMatch"),
			path: ["confirmPassword"],
		});

type PasswordFormData = z.infer<ReturnType<typeof createPasswordFormSchema>>;

const RESET_PASSWORD_CHECKLIST_MIN_LENGTH = 12;

function ResetPasswordRequirements({ value }: { value: string }) {
	const { t } = useAppTranslation();

	const items = useMemo(() => {
		const flags = evaluatePasswordRequirementFlags(
			value,
			RESET_PASSWORD_CHECKLIST_MIN_LENGTH,
		);
		const labelFor = (id: PasswordRequirementRuleId): string => {
			switch (id) {
				case "minLength":
					return t("passwordRequirements.minLength", {
						count: RESET_PASSWORD_CHECKLIST_MIN_LENGTH,
					});
				case "lowercase":
					return t("passwordRequirements.lowercase");
				case "uppercase":
					return t("passwordRequirements.uppercase");
				case "number":
					return t("passwordRequirements.number");
				case "symbol":
					return t("passwordRequirements.symbol");
			}
		};
		return PASSWORD_REQUIREMENT_RULE_ORDER.map((id) => ({
			id,
			label: labelFor(id),
			met: flags[id],
		}));
	}, [value, t]);

	return (
		<PasswordRequirementsChecklist
			className="mt-1"
			ariaLabel={t("passwordRequirements.listLabel")}
			items={items}
		/>
	);
}

export const Route = createFileRoute("/reset-password")({
	validateSearch: searchSchema,
	component: ResetPasswordPage,
});

function ResetPasswordPage() {
	const { t } = useAppTranslation();
	const { state, resetToken, userId } = Route.useSearch();
	const navigate = useNavigate();
	const resetPasswordMutation = useResetPassword();

	const [submitState, setSubmitState] = useState<"idle" | "success" | "error">(
		"idle",
	);

	const newPasswordSchema = useMemo(() => createNewPasswordSchema(t), [t]);
	const passwordFormSchema = useMemo(() => createPasswordFormSchema(t), [t]);

	const newPasswordValidators = useMemo(
		() => ({
			onChange: ({ value }: { value: string }) => {
				if (!value) return undefined;
				const result = newPasswordSchema.safeParse(value);
				if (result.success) return undefined;
				return result.error.issues[0]?.message;
			},
			onChangeAsyncDebounceMs: 500,
			onChangeAsync: async ({ value }: { value: string }) => {
				// Only call the API when basic requirements are already satisfied
				if (!newPasswordSchema.safeParse(value).success) return undefined;
				const result = await userService.validatePassword(userId, value);
				if (result.isValid) return undefined;
				const msg = result.error?.trim();
				return msg || undefined;
			},
		}),
		[userId, newPasswordSchema],
	);

	const form = useAppForm({
		defaultValues: {
			newPassword: "",
			confirmPassword: "",
		} satisfies PasswordFormData,
		validators: {
			onSubmit: ({ value }: { value: PasswordFormData }) => {
				const result = passwordFormSchema.safeParse(value);
				if (!result.success) {
					const fieldErrors: Record<string, string> = {};
					for (const issue of result.error.issues) {
						const path = issue.path[0] as string;
						if (!fieldErrors[path]) {
							fieldErrors[path] = issue.message;
						}
					}
					return fieldErrors;
				}
				return undefined;
			},
		},
		onSubmit: async ({ value }: { value: PasswordFormData }) => {
			try {
				await resetPasswordMutation.mutateAsync({
					userId: userId,
					data: {
						resetPasswordToken: resetToken,
						newPassword: value.newPassword,
					},
				});
				setSubmitState("success");
			} catch (error) {
				console.error("Password reset failed:", error);
				setSubmitState("error");
			}
		},
	});

	// Render success state
	if (submitState === "success") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={CheckCircle}
								color="success"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("resetPassword.success.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("resetPassword.success.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render submit error state
	if (submitState === "error") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("resetPassword.submitError.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("resetPassword.submitError.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => setSubmitState("idle")}
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render expired state
	if (state === "expired") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="warning"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("resetPassword.expired.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("resetPassword.expired.description")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Render invalid state
	if (state === "invalid") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("resetPassword.invalid.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("resetPassword.invalid.description")}
						</p>
						<Button
							color="primary"
							size="lg"
							className="min-w-xs"
							onClick={() => navigate({ to: "/login" })}
						>
							{t("login.signIn")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Render error state (generic)
	if (state === "error") {
		return (
			<div className="flex flex-col min-h-screen overflow-hidden">
				<HeaderNavigation
					onUserProfile={() => {}}
					items={[]}
					name={""}
					email={""}
					onSignOut={() => {}}
					homeUrl={""}
					showAvatarDropdown={false}
				/>
				<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
					<div className="w-full max-w-sm text-center">
						<div className="mb-6 flex justify-center">
							<FeaturedIcon
								icon={AlertCircle}
								color="error"
								theme="modern"
								size="xl"
							/>
						</div>
						<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
							{t("resetPassword.error.title")}
						</h1>
						<p className="mb-8 text-md text-fg-secondary">
							{t("resetPassword.error.description")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Render valid state with form (default)
	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<HeaderNavigation
				onUserProfile={() => {}}
				items={[]}
				name={""}
				email={""}
				onSignOut={() => {}}
				homeUrl={""}
				showAvatarDropdown={false}
			/>
			<div className="flex flex-1 items-center justify-center bg-background-primary px-6 py-10">
				<div className="w-full max-w-sm text-center">
					<div className="mb-6 flex justify-center">
						<FeaturedIcon
							icon={LockKeyholeCircle}
							color="brand"
							theme="modern"
							size="xl"
						/>
					</div>
					<h1 className="mb-3 text-display-sm font-semibold text-fg-primary">
						{t("resetPassword.valid.title")}
					</h1>
					<p className="mb-8 text-md text-fg-secondary">
						{t("resetPassword.valid.description")}
					</p>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4 text-left"
					>
						<form.AppField
							name="newPassword"
							validators={newPasswordValidators}
						>
							{(field: any) => (
								<field.InputField
									field={field}
									type="password"
									label={t("resetPassword.form.newPassword")}
									placeholder={t("resetPassword.form.newPasswordPlaceholder")}
									autoComplete="new-password"
								/>
							)}
						</form.AppField>

						<form.AppField
							name="confirmPassword"
							validators={{
								onChangeListenTo: ["newPassword"],
								onChange: ({
									value,
									fieldApi,
								}: {
									value: string;
									fieldApi: any;
								}) => {
									if (!value) return undefined;
									const newPassword =
										fieldApi.form.getFieldValue("newPassword");
									return value === newPassword
										? undefined
										: "Passwords do not match";
								},
							}}
						>
							{(field: any) => (
								<>
									<field.InputField
										field={field}
										type="password"
										label={t("resetPassword.form.confirmPassword")}
										placeholder={t(
											"resetPassword.form.confirmPasswordPlaceholder",
										)}
										autoComplete="new-password"
									/>
									<form.Subscribe
										selector={(state) => state.values.newPassword}
									>
										{(newPassword) => (
											<ResetPasswordRequirements value={newPassword} />
										)}
									</form.Subscribe>
								</>
							)}
						</form.AppField>

						<form.Subscribe
							selector={(state) => ({
								canSubmit: state.canSubmit,
								isSubmitting: state.isSubmitting,
								isValidating: state.isValidating,
								values: state.values,
								fieldMeta: state.fieldMeta,
							})}
						>
							{({
								canSubmit,
								isSubmitting,
								isValidating,
								values,
								fieldMeta,
							}) => {
								// Check both fields have values
								const hasValues =
									!!values.newPassword && !!values.confirmPassword;

								// Check both fields are valid (no errors and have been validated)
								const newPasswordMeta = fieldMeta.newPassword;
								const confirmPasswordMeta = fieldMeta.confirmPassword;

								const fieldsAreValid =
									newPasswordMeta?.isValid &&
									confirmPasswordMeta?.isValid &&
									!newPasswordMeta?.isValidating &&
									!confirmPasswordMeta?.isValidating;

								// Button is enabled only when all conditions are met
								const isEnabled =
									canSubmit &&
									hasValues &&
									fieldsAreValid &&
									!isSubmitting &&
									!isValidating;

								return (
									<Button
										type="submit"
										color="primary"
										size="lg"
										className="w-full mt-6"
										isDisabled={!isEnabled}
									>
										{isSubmitting
											? t("common.actions.loading")
											: newPasswordMeta?.isValidating
												? "Validating..."
												: t("resetPassword.form.submit")}
									</Button>
								);
							}}
						</form.Subscribe>
					</form>
				</div>
			</div>
		</div>
	);
}
